'use es6';

import { createSelector } from 'reselect';
import SeamlessImmutable from 'seamless-immutable';
import Annotation from 'react-codemirror/model/Annotation';
import AnnotationType from 'react-codemirror/enum/AnnotationType';
import { EMPTY_ARRAY } from 'DesignData/lib/array';
import CodeEditorError from 'DesignData/models/validation/CodeEditorError';
import * as bufferSaveErrorTypes from 'DesignData/enums/bufferSaveErrorTypes';
import { ERROR as EDITOR_ERROR, WARNING, INFO, SUCCESS } from 'DesignData/enums/EditorErrorTypes';
import { getEslintDemoErrors } from 'DesignData/selectors/javaScriptLinter';

const isCodeEditorError = err => err instanceof CodeEditorError;

const filterError = err => err.type === EDITOR_ERROR;

const filterWarning = err => err.type === WARNING;

const filterInfo = err => err.type === INFO;

const filterSuccess = err => err.type === SUCCESS;
/* IE11 Does not support new Set([iterable]) */


const bufferSaveErrors = Object.keys(bufferSaveErrorTypes).reduce((set, type) => {
  set.add(type);
  return set;
}, new Set());
export const getCodeHublErrorsById = (state, props) => state.editorErrors.codeHublErrorsById[props.assetId] || EMPTY_ARRAY;
export const getCodeCssErrorsById = (state, props) => state.editorErrors.codeCssErrorsById[props.assetId] || EMPTY_ARRAY;
export const getCodeJsErrorsById = (state, props) => state.editorErrors.codeJsErrorsById[props.assetId] || EMPTY_ARRAY;
export const getErrorsById = (state, props) => state.editorErrors.errorsById[props.assetId] || EMPTY_ARRAY;
export const getWarningsById = (state, props) => state.editorErrors.warningsById[props.assetId] || EMPTY_ARRAY;
export const getInfoById = (state, props) => state.editorErrors.infoById[props.assetId] || EMPTY_ARRAY;
export const getSuccessesById = (state, props) => state.editorErrors.successesById[props.assetId] || EMPTY_ARRAY;
/**
 * Required Props
 *  assetId
 */

function createCombinedErrorsListSelector(filter, errorSelectors) {
  return createSelector(errorSelectors, (...errors) => {
    const list = errors.reduce((totalList, errList) => {
      return totalList.concat(errList.filter(filter));
    }, []);
    list.sort(CodeEditorError.sorter);
    return list;
  });
}

export const makeGetPrimaryErrors = () => {
  return createCombinedErrorsListSelector(filterError, [getErrorsById, getCodeHublErrorsById]);
};
export const makeGetPrimaryWarnings = () => {
  return createCombinedErrorsListSelector(filterWarning, [getWarningsById, getCodeHublErrorsById]);
};
export const makeGetPrimaryInfo = () => {
  return createCombinedErrorsListSelector(filterInfo, [getInfoById, getCodeHublErrorsById]);
};
export const makeGetPrimarySuccesses = () => {
  return createCombinedErrorsListSelector(filterSuccess, [getSuccessesById, getCodeHublErrorsById]);
};
export const makeGetCombinedErrorsById = () => {
  return createCombinedErrorsListSelector(filterError, [getErrorsById, getCodeHublErrorsById, getCodeJsErrorsById, getCodeCssErrorsById]);
};
export const makeGetCombinedWarningsById = () => {
  return createCombinedErrorsListSelector(filterWarning, [getWarningsById, getCodeHublErrorsById, getCodeJsErrorsById, getCodeCssErrorsById]);
};

const createLintedCodeMessagesSelector = selector => {
  const determineList = (old, next) => {
    if (next.length === 0) {
      return EMPTY_ARRAY;
    }

    const sortedNext = next.sort(CodeEditorError.sorter);

    if (old.length === sortedNext.length && old.every((err, index) => err === sortedNext[index])) {
      return old;
    }

    return sortedNext;
  };

  let data = SeamlessImmutable({
    errors: EMPTY_ARRAY,
    warnings: EMPTY_ARRAY,
    info: EMPTY_ARRAY,
    successes: EMPTY_ARRAY
  });
  return createSelector([selector], errs => {
    const errors = [];
    const warnings = [];
    const info = [];
    const successes = [];
    errs.forEach(err => {
      switch (err.type) {
        case EDITOR_ERROR:
          errors.push(err);
          break;

        case WARNING:
          warnings.push(err);
          break;

        case INFO:
          info.push(err);
          break;

        case SUCCESS:
          successes.push(err);
          break;

        default:
          break;
      }
    });
    data = SeamlessImmutable.merge(data, {
      errors: determineList(data.errors, errors),
      warnings: determineList(data.warnings, warnings),
      info: determineList(data.info, info),
      successes: determineList(data.successes, successes)
    });
    return data;
  });
};

export const makeGetLintedJavaScriptMessages = () => {
  return createLintedCodeMessagesSelector(getCodeJsErrorsById);
};
export const makeGetLintedCssMessages = () => {
  return createLintedCodeMessagesSelector(getCodeCssErrorsById);
};
export const getEslintDemoMessages = createLintedCodeMessagesSelector(getEslintDemoErrors); // TODO: This can be done better: editorErrorsActions.setHublErrors in component on change.

export const makeGetCodeEditorErrorsById = () => {
  return createSelector([getErrorsById, getWarningsById], (errors = EMPTY_ARRAY, warnings = EMPTY_ARRAY) => {
    return errors.concat(warnings).filter(isCodeEditorError);
  });
};
export const getAllAnnotationsForBufferSave = createSelector([getErrorsById, getWarningsById, getInfoById], (errors, warnings, info) => {
  return errors.concat(warnings, info).filter(__error => bufferSaveErrors.has(__error.errorType));
});

const getSessionId = (state, props) => props.sessionId;

export const makeGetSourceErrors = () => {
  const getSourceErrors = makeGetCodeEditorErrorsById();
  return createSelector([getSourceErrors, getSessionId], (sourceErrors, sessionId) => sourceErrors.filter(err => err.sessionId === sessionId));
};
/**
 * Required Props:
 *  - assetId
 *  - sessionId
 *
 * Creates `react-codemirror` annotations object list.
 * @see {@link https://git.hubteam.com/HubSpot/react-codemirror/blob/master/static/js/model/Annotation.js}
 */

export const makeGetCodeMirrorAnnotations = () => {
  return createSelector([getCodeJsErrorsById, getSessionId], (codeJsErrors, sessionId) => {
    if (!(codeJsErrors && codeJsErrors.length)) {
      return EMPTY_ARRAY;
    }

    const annos = [];
    codeJsErrors.forEach(err => {
      if (sessionId == null || err.sessionId !== sessionId) {
        return;
      }

      annos.push(new Annotation({
        lineNumber: err.lineNumber,
        column: err.column,
        message: err.message,
        evidence: err.evidence,
        type: AnnotationType.ERROR
      }));
    });
    return SeamlessImmutable(annos);
  });
};